import { Tags } from "lucide-react";
import {
  FormatButtons,
  LinkButtons,
  ListButtons,
  RichTextInput,
  RichTextInputToolbar,
} from "ra-input-rich-text";
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  ImageField,
  ImageInput,
  List,
  NumberField,
  NumberInput,
  SimpleForm,
  TextField,
  TextInput,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { CardListActions, EditorToolbar, validation } from "../../components";

const validate = {
  name: [validation.required(), validation.minLength(2)],
  order: [validation.required(), validation.number()],
  parent: [validation.required(), validation.number()],
  parent_order: [validation.required(), validation.number()],
};

const filters = [
  <BooleanInput
    label="Active"
    source="display_active"
    alwaysOn
    format={(value) => value === 1}
    parse={(value) => (value === true ? 1 : 0)}
  />,
  <TextInput source="parent" alwaysOn />,
  <TextInput source="id" alwaysOn />,
  <TextInput label="Name" source="name" alwaysOn />,
];

function renderEditForm() {
  return (
    <SimpleForm
      toolbar={<EditorToolbar />}
      onChange={(value) =>
        (value.slug =
          value.name && value.name.replace(/\s/g, "-").toLowerCase())
      }
    >
      <ImageField
        source="image"
        title="Image"
        style={{
          float: "right",
          width: "70%",
        }}
      />
      <TextInput source="name" label="Name" validate={validate.name} />
      <TextField source="slug" label="Slug" />
      <SanitizedP>
        Order places a topic in a certain order on the topics page. A lower
        number will place it higher on the page. All topics with the same order
        number will be ordered in Alphabetical order. Also affects sub-topic
        placement, relative to other "sibling" sub-topics e.g. Parasha
        placement. Typical topic order is 3.
      </SanitizedP>
      <NumberInput
        source="order"
        label="Order"
        validate={validate.order}
        defaultValue={3}
      />
      <SanitizedP>
        Parent topic. All sub-topics have their parent topic set here. All
        parent topics on the site have parent set to 6.
      </SanitizedP>
      <NumberInput
        source="parent"
        label="Parent"
        validate={validate.parent}
        defaultValue={6}
      />
      <SanitizedP>
        Parent Order is used to "tell" the site whether there are sub-topics.
        E.g. Parasha has a parent order of 23. The pattern, when adding a new
        parent category, is to use the category ID as the parent order. All
        other topics should be set to -1.
      </SanitizedP>
      <NumberInput
        source="parent_order"
        label="Parent Order"
        validate={validate.parent_order}
        defaultValue={-1}
      />
      <BooleanInput source="display_active" label="Active" defaultValue={1} />
      <TextInput
        source="image"
        label="Image File Name"
        title="(Clear this field and click Save to remove the image.)"
      />
      <ImageInput
        source="FILE_image"
        label="Image Upload"
        accept="image/*"
        placeholder={<p>Click or drop your file here.</p>}
        touch="true"
      >
        <ImageField source="src" title="title" />
      </ImageInput>
      <NumberInput source="dial_in_ext" label="Dial In Extension" />

      <h4>JewishAnytime info</h4>
      <NumberInput
        source="ja_parent"
        label="JewishAnytime Parent"
        defaultValue={null}
      />
      <TextInput
        source="ja_icon"
        label="JewishAnytime Icon"
        defaultValue={null}
      />
      <TextInput
        source="english_name"
        label="English Display Name"
        defaultValue={null}
      />
      <RichTextInput
        width={"100%"}
        source="desc"
        toolbar={
          <RichTextInputToolbar>
            <FormatButtons size="large" />
            <ListButtons size="large" />
            <LinkButtons size="large" />
          </RichTextInputToolbar>
        }
      />
    </SimpleForm>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Dedication created successfully!", { type: "success" });
          refresh(); // Ensures UI updates with the new data
          redirect("list", "categories"); // Redirect back to list after update
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {/* <Create {...props} redirect="edit"> */}
      {renderEditForm()}
    </Create>
  );
}
// SanitizedP is how we resolve the below issue for <p></p> DOM element
// Warning: React does not recognize the `basePath` prop on a DOM element.  If you
// accidentally passed it from a parent component, remove it from the DOM
// element.
const SanitizedP = ({ basePath, ...props }) => {
  return <p {...props} />;
};
function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Category updated successfully!", { type: "success" });
          refresh(); // Ensures UI updates with the new data
          redirect("list", "categories"); // Redirect back to list after update
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {/* <Edit {...props} redirect={false} undoable={false}> */}
      {renderEditForm()}
    </Edit>
  );
}

function ListView(props) {
  return (
    <List
      {...props}
      actions={<CardListActions exporter={true} />}
      filterDefaultValues={{ display_active: 1 }}
      filters={filters}
      // perPage={25}

      title="Categories"
    >
      <Datagrid rowClick="edit">
        <NumberField source="id" label="Id" />
        <ImageField source="image_url" label="Image" sortBy="image" />
        <TextField source="name" label="Name" />
        <TextField source="slug" label="Slug" />
        <NumberField source="order" label="Order" />
        <NumberField source="parent" label="Parent" />
        <NumberField source="ja_parent" label="Jewish Anytime Parent" />
        <NumberField source="parent_order" label="Parent Order" />
        <BooleanField source="display_active" label="Active" />
      </Datagrid>
    </List>
  );
}

export const Categories = {
  category: "database",
  name: "categories",
  create: CreateView,
  edit: EditView,
  icon: Tags,
  list: ListView,
  options: { label: "Categories" },
};
